<script>
import bem from '@/utils/bem';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  DeleteOutlined,
  AppstoreOutlined,
} from '@ant-design/icons-vue';

const b = bem('option-editor');

export default {
  components: {
    DeleteOutlined,
    AppstoreOutlined,
  },
  props: {
    option: { type: Object, required: true },
  },
  emits: ['openDisplay', 'updated'],
  data() {
    return {
      // Local editable copy of option fields — prevents direct mutation of prop
      form: { ...this.option },
      orientationOptions: [
        { value: 'vertical', label: 'Vertical' },
        { value: 'horizontal', label: 'Horizontal' },
      ],
    };
  },
  computed: {
    ...mapState({
      frameOptionDisplays: (state) => state.createTemplate.frameOptionDisplays,
      frameColors: (state) => state.createTemplate.colors,
    }),
    ...mapGetters({
      frameColorsGetter: 'createTemplate/frameColors',
    }),
    // Colors available for this option's size
    colors() {
      const sizeId = this.option.frameSize?.id;
      return sizeId ? this.frameColorsGetter(sizeId) : [];
    },
    // Current color and orientation display
    currentColorName() {
      return this.option.frameColor?.name || 'n/a';
    },
    currentOrientation() {
      return this.option.frameOrientation || 'n/a';
    },
    currentSizeName() {
      return this.option.frameSize?.name || 'n/a';
    },
  },
  watch: {
    // Re-sync form when option prop changes (user selects different option in tree)
    option: {
      handler(newVal) {
        this.form = { ...newVal };
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      updateFrameOptionAction: 'createTemplate/updateFrameOption',
      deleteFrameOptionAction: 'createTemplate/deleteFrameOption',
      getFrameOptionDisplaysAction: 'createTemplate/getFrameOptionDisplays',
    }),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    async saveOption() {
      const payload = {
        type: this.form.name,
        productId: this.form.parentId,
        optionId: this.form.id,
        option: {
          displayName: this.form.name,
          price: this.form.price,
          additionalDiscount: this.form.additionalDiscount,
          inStock: this.form.inStock,
          isDefaultOption: this.form.isDefaultOption,
          isDefaultMinOption: this.form.isDefaultMinOption,
          isPersonalize: this.form.isPersonalize,
          frameColorId: this.form.frameColor?.id || this.form.frameColor,
          frameSizeId: this.form.frameSize?.id || this.form.frameSize,
          frameOrientation: this.form.frameOrientation,
          isTaxApplied: this.form.isTaxApplied ?? true,
          personalizeImage: { imageKey: '', imageUri: '' },
        },
      };
      await this.updateFrameOptionAction(payload);
      this.$emit('updated');
    },
    async onDelete() {
      await this.deleteFrameOptionAction({
        optionId: this.option.id,
        frameId: this.option.parentId,
        type: this.option.name,
      });
      this.$emit('updated');
    },
    onDisplayClick(display) {
      this.$emit('openDisplay', display.id);
    },
  },
  async mounted() {
    // Load displays for this option to show summary
    await this.getFrameOptionDisplaysAction({ productId: this.option.id });
  },
};
</script>

<template>
  <div :class="b()">
    <div :class="b('header')">
      <h3 :class="b('title')">
        {{ currentSizeName }} {{ currentOrientation }} {{ currentColorName }}
      </h3>
      <div :class="b('actions')">
        <a-button type="primary" @click="saveOption">Save changes</a-button>
        <a-popconfirm
          title="Are you sure you want to delete this option?"
          ok-text="Yes"
          cancel-text="No"
          @confirm="onDelete"
        >
          <a-button danger>
            <template #icon><DeleteOutlined /></template>
            Delete
          </a-button>
        </a-popconfirm>
      </div>
    </div>

    <!-- Option fields -->
    <div :class="b('fields')">
      <div :class="b('row')">
        <div :class="b('field')">
          <span :class="b('label')">Orientation</span>
          <a-select
            v-model:value="form.frameOrientation"
            :options="orientationOptions"
            style="width: 100%"
          />
        </div>
        <div :class="b('field')">
          <span :class="b('label')">Color</span>
          <a-select
            v-model:value="form.frameColor"
            :options="colors"
            style="width: 100%"
            :field-names="{ label: 'label', value: 'value' }"
          />
        </div>
      </div>

      <div :class="b('row')">
        <div :class="b('field')">
          <span :class="b('label')">Price</span>
          <a-input v-model:value="form.price" type="number" />
        </div>
        <div :class="b('field')">
          <span :class="b('label')">Discount</span>
          <a-input v-model:value="form.additionalDiscount" type="number" />
        </div>
      </div>

      <div :class="b('toggles')">
        <div :class="b('toggle')">
          <span>Stock</span>
          <a-switch v-model:checked="form.inStock" />
        </div>
        <div :class="b('toggle')">
          <span>Default</span>
          <a-switch v-model:checked="form.isDefaultOption" />
        </div>
        <div :class="b('toggle')">
          <span>Personalize</span>
          <a-switch v-model:checked="form.isPersonalize" />
        </div>
        <div :class="b('toggle')">
          <span>Min Def</span>
          <a-switch v-model:checked="form.isDefaultMinOption" />
        </div>
        <div :class="b('toggle')">
          <span>Tax applied</span>
          <a-switch v-model:checked="form.isTaxApplied" />
        </div>
      </div>
    </div>

    <!-- Displays summary -->
    <div :class="b('displays')">
      <h4 :class="b('section-title')">
        Displays ({{ frameOptionDisplays.length }})
      </h4>
      <div :class="b('displays-grid')">
        <div
          v-for="display in frameOptionDisplays"
          :key="display.id"
          :class="b('display-card')"
          role="button"
          tabindex="0"
          @click="onDisplayClick(display)"
          @keydown.enter="onDisplayClick(display)"
        >
          <div :class="b('display-preview')">
            <img
              v-if="display.baseImageUri"
              :class="b('display-thumb')"
              :src="display.baseImageUri"
              :alt="display.name"
            />
            <AppstoreOutlined v-else style="font-size: 32px; color: var(--Text-Secondary)" />
          </div>
          <span :class="b('display-name')">{{ display.name }}</span>
          <a-tag v-if="display.isDefault" color="blue" size="small">Default</a-tag>
        </div>
      </div>
      <a-empty
        v-if="frameOptionDisplays.length === 0"
        description="No displays configured"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';

.option-editor {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 20px;
    font-weight: 600;
    color: var(--Text-Primary);
    margin: 0;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }

  &__fields {
    background: var(--Background-Secondary);
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 32px;
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--Text-Secondary);
  }

  &__toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding-top: 8px;
    border-top: 1px solid var(--Border-Light);
  }

  &__toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--Text-Primary);
  }

  &__section-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--Text-Primary);
    margin-bottom: 16px;
  }

  &__displays-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }

  &__display-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 12px;
    background: var(--Background-Secondary);
    border-radius: 8px;
    border: 1px solid var(--Border-Light);
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover {
      border-color: var(--Button-Primary);
    }
  }

  &__display-preview {
    width: 100%;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--Background-Tertiary);
    border-radius: 4px;
    overflow: hidden;
  }

  &__display-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__display-name {
    font-size: 13px;
    text-align: center;
    color: var(--Text-Primary);
  }
}
</style>
